const study = {
  meta: {
    slug: "real-estate-bridge-720k-atlanta-fix-and-flip",
    title:
      "$720K in 11 days: How an Atlanta investor bridged a fix-and-flip when the bank timeline collapsed",
    excerpt:
      "A seasoned flipper had a signed contract on a distressed intown property. Ten days before close, the community bank he'd used for eight years put a 60-day hold on new commercial originations. Here's the bridge structure we ran, and why the borrower actively chose not to save money.",
    industry: "Real estate · Fix-and-flip investor",
    product: "Bridge Loan (interest-only) → sale payoff",
    amount: "$720K",
    term: "9 months (interest-only)",
    timeToFund: "11 days",
    state: "Georgia",
    challenge: "Speed-critical acquisition · Lender fallout",
    image:
      "https://images.pexels.com/photos/590041/pexels-photo-590041.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-07-19",
    readTime: "6 min read",
    tags: ["real-estate"],
    relatedArticles: [],
  },
  situation: `An Atlanta-area investor with an eight-year track record — 34 flips completed, average hold of 5.4 months, average net margin of 22%. A well-run one-person operation with a dedicated GC crew, a real-estate attorney on retainer, and a community bank that had funded 19 of his last 20 acquisitions with a soft-quoted line ready to draw.

The property: a **1926 intown craftsman**, 3,100 square feet, listed as a distressed sale after a probate dispute finally cleared. Contract price **$780K**, appraised as-is at $795K, ARV (after-repair value) at $1.42M. Close date: 14 days from LOI acceptance.

**Day 4 after LOI**: the community bank's president called personally. Regulatory examination had flagged their commercial real-estate concentration; effective immediately, no new originations for 60 days. Every existing borrower in queue was frozen, our client included.

He had **10 days to close or forfeit a $50K earnest-money deposit**, plus lose a deal he'd tracked for two years.`,
  structure: `**Why a bridge, not a conventional flip loan.** Under a normal timeline our client would have used a **residential investment loan** (12–24-month DSCR-based product) — but those close in 21–28 days minimum, and the earnest-money clock was 10 days. Under a shorter timeline he'd have used a **hard-money flip loan** — those close in 5–7 days but price at 12–14% with 3–5 points at close, and the exit assumption is a sale within 6 months.

The right product was a **bridge loan** — a short-term, interest-only, real-estate-secured loan priced between the two, specifically designed for the "I need to close now and refinance or exit within 12 months" scenario:

- **$720K bridge**, first-position lien on the property
- **9-month term**, interest-only monthly payments, balloon at maturity
- **~11.5% annualized rate**, 2 points at close ($14,400)
- **No prepayment penalty** — critical, because the exit could be month 4 or month 8 depending on renovation timeline
- **60% loan-to-cost** ($720K loan on $1.2M all-in projected basis: purchase + rehab), leaving 40% equity for lender comfort

**Documents we drove:**

1. Purchase agreement + earnest-money receipt
2. Two comparable sale reports for the ARV assumption (we pulled these ourselves the same afternoon from MLS)
3. Rehab budget with GC signature ($382K itemized)
4. Two years of tax returns + a portfolio spreadsheet showing the 34 prior flips (this is what unlocked the pricing — the bridge lender priced this file 2 points below their sheet on strength of the track record)

**Why the borrower chose not to save money.** After the bridge quote came in, we ran a parallel proposal from a hard-money lender at 10.75% and 4 points — nominally cheaper if we assumed a fast exit. Two reasons our client passed:

1. **The 4 points cost $28,800 at close** vs. the bridge's $14,400 — savings of 0.75% on rate need to overcome $14,400 in upfront cost, which requires the loan to be outstanding for **10+ months** to pencil. If he exited faster (which he expected), the bridge wins outright.
2. **The hard-money lender's default clause was cleaner-looking on paper but 4x harsher in practice** — a 30-day cure window vs. 90 days, plus a personal deficiency exposure the bridge product waived at the 60% LTC level.

The right product is not always the lowest rate on the quote sheet.`,
  outcome: `**Bridge funded on day 11.** Property closed on day 12 — two days before earnest money would have been forfeited. Renovation began day 15.

Timeline from there:

- **Interior renovation complete** by month 4 (plumbing/electrical rebuild + full kitchen and two baths)
- **Listed at $1.395M** in month 5 — priced $25K under initial ARV projection for speed
- **Under contract in 9 days** at $1.36M
- **Closed at day 189 (~6.2 months)** — well inside the 9-month bridge term
- **Bridge paid off in full** with zero prepayment penalty

Net numbers on the deal:

- **Sale price**: $1,360,000
- **All-in cost**: $780K purchase + $382K rehab + $46K carrying costs (interest, taxes, insurance) + $14K origination + closing = **$1,222K**
- **Gross profit**: $138K on ~$180K cash-in (rehab + carrying), for a **~77% cash-on-cash return** in 6.2 months

The lesson: **when the bank walks, don't panic-refi into the first quote that closes fastest.** Bridge products exist specifically for scenarios where an 8-year borrower with a track record hits a timeline problem the incumbent lender can no longer solve. The right product for a 6-month hold is not the cheapest annualized rate — it's the one whose total cost structure aligns with your actual timeline.`,
};

export default study;
